import React, { useState, useEffect, useRef } from "react";
import {
  CardActionArea,
  Checkbox,
  Divider,
  makeStyles,
  Grid,
  Typography,
  CardMedia,
  TextField,
  Button,
  CircularProgress,
  useMediaQuery,
  Box,
  Container,
} from "@material-ui/core";
import { Pagination } from "@material-ui/lab";
import { useForm, Controller, useWatch } from "react-hook-form";
import { useMutation, useQuery, useLazyQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import "react-responsive-carousel/lib/styles/carousel.min.css"; // requires a loader
import { Carousel } from "react-responsive-carousel";

import { Card, CardActions, CardContent } from "../../components/Card";
import Text from "../../components/Text";
import { TextInput } from "../../components/Form";
import { useAlert } from "../../components/Alert";
import LoadingModal from "../../components/LoadingModal";

const GET_EXCLUSIVEPROPERTIES = gql`
  query exclusiveProperties(
    $franchiseStoreId: Int!
    $searchTerm: String
    $latest: Boolean
    $pageSize: Int
    $page: Int
  ) {
    exclusiveProperties(
      franchiseStoreId: $franchiseStoreId
      searchTerm: $searchTerm
      latest: $latest
      pageSize: $pageSize
      page: $page
    ) {
      count
      pageCount
      contents {
        id
        obj_court
        obj_str
        obj_share
        obj_caseno
        obj_another
        obj_time
        obj_date_ymd
        obj_date_hi
        obj_city
        obj_area
        obj_section
        obj_section_s
        obj_landno
        obj_address
        obj_link
        obj_margin
        obj_price
        obj_price_set
        obj_creditor
        obj_debtor
        obj_bulletin_data
        obj_remark
        obj_land_data
        obj_build_data
        obj_land_floor
        obj_build_floor
        obj_add_floor
        obj_s_floor
        obj_c_floor
        obj_total_floor
        obj_transfer
        obj_type
        obj_creat_date
        obj_stop
        obj_tender_num
        obj_tender
        obj_check_num
        obj_stop_link
        obj_source
        obj_marker
        obj_coordinates
        obj_request_num
        obj_cadastral
        obj_patterns
        pricePerBuildPing
        pricePerLandPing
        obj_sql
        obj_pyPDF
        phppdfjoin
        errorLog
        lastEditedBy
        images {
          id
          imageUrl
        }
        imageHasCreated
        source
        name
        rent
        deposit
        managementFee
        monthOfManagementFee
        parkingSpace
        houseAge
        indoor_floor
        room
        hall
        bathroom
        orientation
        enable
      }
    }
  }
`;

const ENABLE_EXCLUSIVEPROPERTY = gql`
  mutation enableExclusiveProperty($id: Int!, $enabled: Boolean!) {
    enableExclusiveProperty(id: $id, enabled: $enabled)
  }
`;

const DELETE_EXCLUSIVEPROPERTY = gql`
  mutation deleteExclusiveProperty($id: Int!) {
    deleteExclusiveProperty(id: $id)
  }
`;

export default function PublicationTable({ id, onEdit = () => {} }) {
  const Alert = useAlert();
  const { handleSubmit, control, setValue, getValues, watch } = useForm({
    defaultValues: {
      searchTerm: "",
      page: 1,
    },
  });
  const page = useWatch({ name: "page", control });

  const searchTerm = useWatch({ name: "searchTerm", control });
  const { data, refetch, loading } = useQuery(GET_EXCLUSIVEPROPERTIES, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    variables: {
      franchiseStoreId: id,
      searchTerm,
      latest: true,
      pageSize: 4,
      page: page,
    },
  });

  const [deleteExclusiveProperty, { loading: deleteExclusivePropertyLoading }] =
    useMutation(DELETE_EXCLUSIVEPROPERTY, {
      onCompleted({ deleteExclusiveProperty }) {
        if (deleteExclusiveProperty) {
          Alert.notice(`刪除成功`);
          return refetch();
        } else return Alert.notice(`刪除失敗`);
      },
    });

  const [enableExclusiveProperty, { loading: enableExclusivePropertyLoading }] =
    useMutation(ENABLE_EXCLUSIVEPROPERTY, {
      onCompleted({ enableExclusiveProperty }) {
        if (enableExclusiveProperty) {
          Alert.notice(`上架成功`);
          return refetch();
        } else return Alert.notice(`上架失敗`);
      },
    });

  return (
    <Box>
      <LoadingModal
        loading={
          deleteExclusivePropertyLoading || enableExclusivePropertyLoading
        }
      />
      <CardContent>
        <Grid container spacing={1}>
          <Grid item>
            <Typography color="primary">搜尋</Typography>
            <Controller
              name="searchTerm"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextInput {...field} placeholder={"地址"} />
              )}
            />
          </Grid>
        </Grid>
      </CardContent>
      <Divider />
      <LoadingModal loading={loading} />
      {!!data && !!data.exclusiveProperties ? (
        <Grid container>
          {data.exclusiveProperties.contents.map((item) => {
            return (
              <Grid item xs={12} sm={6}>
                <CardContent>
                  <Grid container spacing={1}>
                    <Grid item xs={6} sm={6}>
                      <CardActionArea>
                        {item.images.length > 0 ? (
                          <CarouselImages
                            data={item.images}
                            onChangeId={() => {
                              onEdit(item.id);
                            }}
                          />
                        ) : (
                          <CardMedia
                            component="img"
                            image={require("../../images/test.PNG").default}
                            style={{
                              height: 250,
                              width: "100%",
                              objectFit: "fill",
                            }}
                            onClick={() => {
                              onEdit(item.id);
                            }}
                          />
                        )}
                      </CardActionArea>
                    </Grid>
                    <Grid item xs={6} sm={6}>
                      <Grid container direction="column" spacing={1}>
                        <Grid item>
                          <Text type="h5">{item.name}</Text>
                        </Grid>
                        <Grid item>
                          <Typography>
                            {"地址：" +
                              item.obj_city +
                              item.obj_area +
                              item.obj_address}
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Typography>
                            {"總價：" +
                              item.obj_price +
                              "萬\u2002" +
                              "總坪數：" +
                              item.obj_total_floor +
                              "坪"}
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Typography>
                            {`格局：${item.room ?? ""}房 ${item.hall ?? ""}廳 ${
                              item.bathroom ?? ""
                            }衛`}
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Typography>
                            {"類型：" + item.obj_patterns}
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Typography>{`屋齡：${
                            item.houseAge ?? ""
                          }年`}</Typography>
                        </Grid>
                        <Grid item>
                          <Grid container spacing={1}>
                            {!item.enable && (
                              <Grid item>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  onClick={() => {
                                    Alert.alert("通知", "確定要重新上架？", [
                                      {
                                        text: "確定",
                                        onPress: () => {
                                          enableExclusiveProperty({
                                            variables: {
                                              id: item.id,
                                              enabled: true,
                                            },
                                          });
                                        },
                                        type: "ok",
                                      },
                                      {
                                        text: "取消",
                                        type: "cancel",
                                      },
                                    ]);
                                  }}
                                >
                                  重新上架
                                </Button>
                              </Grid>
                            )}
                            <Grid item>
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                  Alert.alert("通知", "確定要刪除？", [
                                    {
                                      text: "確定",
                                      onPress: () => {
                                        deleteExclusiveProperty({
                                          variables: { id: item.id },
                                        });
                                      },
                                      type: "ok",
                                    },
                                    {
                                      text: "取消",
                                      type: "cancel",
                                    },
                                  ]);
                                }}
                              >
                                刪除
                              </Button>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </CardContent>
              </Grid>
            );
          })}
        </Grid>
      ) : null}
      <CardActions>
        <Grid container justifyContent="center">
          <Controller
            control={control}
            name="page"
            render={({ field, fieldState: { error } }) => (
              <Pagination
                count={
                  data?.exclusiveProperties?.pageCount !== 0
                    ? data?.exclusiveProperties?.pageCount
                    : 1
                }
                size="large"
                color="primary"
                page={field.value ?? 1}
                onChange={(e, n) => {
                  field.onChange(n);
                }}
              />
            )}
          />
        </Grid>
      </CardActions>
    </Box>
  );
}

function CarouselImages({ data, onChangeId = () => {} }) {
  const [imageIndex, setImageIndex] = useState(0);
  const isTablet = useMediaQuery("(max-width:960px)");
  const isPhone = useMediaQuery("(max-width:600px)");
  // 輪播圖
  const [images, setImages] = useState([]);
  useEffect(() => {
    setImages([]);
    if (data?.length > 0) {
      let newImages = [];
      data?.forEach((item) => {
        newImages.push(item.imageUrl);
      });
      setImages(newImages);
    }
  }, [data]);
  if (images.length > 0) {
    return (
      <Box>
        <Container maxWidth="sm">
          <Carousel
            autoPlay
            showThumbs={false}
            infiniteLoop={true}
            interval={3000}
            //emulateTouch={true}
            showArrows={false}
            selectedItem={imageIndex}
            onChange={setImageIndex}
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              textAlign: isTablet ? "center" : "",
              position: "relative",
            }}
            onClickItem={onChangeId}
          >
            {images.map((item, index) => (
              <>
                <img
                  src={item}
                  key={index}
                  style={{
                    // 如果要完整顯示改成contain
                    objectFit: "contain",
                    height: 250,
                    width: "100%",
                    // height: isPhone ? "300px" : isTablet ? "550px" : "600px",
                    borderRadius: "10px",
                    padding: "5px",
                  }}
                />
              </>
            ))}
          </Carousel>
        </Container>
      </Box>
    );
  } else {
    return null;
  }
}
